// fogdraw.ts ↔ game/fogmap.cpp's RENDER arm (FogMap::Draw :331-417,
// FogMap::DrawGalaxite :423-538, + the bitmap-loading half of FogMap::Init
// :59-110). The fog CONTENT (m_pbMap bytes, reveal logic, galaxite amounts) is
// sim-owned (sim/src/sim/fogmap.ts); this class draws it: 16 edge tiles indexed
// by the neighbor-mask nibble (3 are NULL — fully-open patterns the tilemap
// already shows), row-run black fills for opaque spans, 9 galaxite density
// sprites, and wall segments shaped by their 4-neighbor mask via wall.anir.

import type { TexAtlasMgr, DibBitmapLike } from './texatlasmgr.ts';
import type { TBitmap } from './tbitmap.ts';
import type { CpuDibBitmap } from './dibbitmap.ts';
import type { PackFileReader } from '../io/packfile.ts';
import type { UpdateMap } from './updatemap.ts';
import type { CoordMap } from './coords.ts';
import { ClientAnimationData } from './animdata.ts';
import { GetColor, iclr } from './palette.ts';

const kbfFogMask = 0x0f;
const kbOpaque = 0;
const kbEmpty = 15;
const kbfGalaxiteMask = 0xf0;
const kcGalaxiteShift = 4;
const kttWall = 2; // ht.h:1552
const ksideNeutral = 0;

// fogmap.cpp:438-440
const s_abGxTranslate = [0, 2, 1, 1, 0, 0, 5, 4, 4, 3, 3, 8, 7, 7, 6, 6];

export class ClientFogDraw {
  private m_aptbm: (TBitmap | null)[] = [];
  private m_aptbmGalax: (TBitmap | null)[] = [];
  private m_panidWalls: ClientAnimationData | null = null;
  private coords: CoordMap;

  constructor(coords: CoordMap) {
    this.coords = coords;
  }

  // fogmap.cpp:59-110 — the render half of FogMap::Init
  InitArt(pakr: PackFileReader, tam: TexAtlasMgr): boolean {
    const fogName = (i: number): string | null => {
      // index = inverted neighbor nibble; 15/9/6 have no art (fully open / checker)
      const names: (string | null)[] = [
        'fog1111.png',
        'fog1110.png',
        'fog1101.png',
        'fog1100.png',
        'fog1011.png',
        'fog1010.png',
        null,
        'fog1000.png',
        'fog0111.png',
        null,
        'fog0101.png',
        'fog0100.png',
        'fog0011.png',
        'fog0010.png',
        'fog0001.png',
        null,
      ];
      return names[i];
    };
    this.m_aptbm = [];
    let cNull = 0;
    for (let n = 0; n < 16; n++) {
      const name = fogName(n);
      const tbm = name === null ? null : tam.CreateTBitmap(name);
      this.m_aptbm.push(tbm);
      if (tbm === null) cNull++;
    }
    if (cNull !== 3) return false;

    this.m_aptbmGalax = [];
    for (const name of [
      'galax1a',
      'galax1b',
      'galax1c',
      'galax2a',
      'galax2b',
      'galax2c',
      'galax3a',
      'galax3b',
      'galax3c',
    ]) {
      const tbm = tam.CreateTBitmap(`${name}.png`);
      if (tbm === null) return false;
      this.m_aptbmGalax.push(tbm);
    }

    this.m_panidWalls = new ClientAnimationData();
    return this.m_panidWalls.Init(pakr, tam, 'wall.anir');
  }

  // fogmap.cpp:331-417
  Draw(
    pbm: CpuDibBitmap,
    xMap: number,
    yMap: number,
    pupd: UpdateMap,
    fogBytes: Uint8Array,
    ctxMap: number,
    ctyMap: number,
  ): void {
    const gcx = this.coords.gcxTile;
    const gcy = this.coords.gcyTile;
    const siz = pbm.GetSize();
    const tx = Math.floor(xMap / gcx);
    const ty = Math.floor(yMap / gcy);
    let ctx = Math.floor((siz.cx + gcx - 1) / gcx) + 1;
    if (tx + ctx > ctxMap) ctx = ctxMap - tx;
    let cty = Math.floor((siz.cy + gcy - 1) / gcy) + 1;
    if (ty + cty > ctyMap) cty = ctyMap - ty;
    const clrBlack = GetColor(iclr.kiclrBlack);

    const invalid = pupd.GetInvalidMap();
    const sizMap = pupd.GetMapSize();
    const cfInvalidNextScan = sizMap.cx - ctx;
    let iInvalid = 0;

    let ibMap = ty * ctxMap + tx;
    const cbNextScan = ctxMap - ctx;
    let xTile = tx * gcx - xMap;
    let yTile = ty * gcy - yMap;
    const xTileStart = xTile;
    for (let tyT = ty; tyT < ty + cty; tyT++) {
      let cEmpty = 0;
      let xStart = 0;
      for (let txT = tx; txT < tx + ctx; txT++) {
        let bFog = fogBytes[ibMap++] & kbfFogMask;
        if (!invalid[iInvalid++]) bFog = kbEmpty;

        if (bFog === kbOpaque) {
          if (cEmpty === 0) xStart = xTile;
          cEmpty++;
          xTile += gcx;
          continue;
        }

        if (cEmpty !== 0) {
          pbm.Fill(xStart, yTile, cEmpty * gcx, gcy, clrBlack);
          cEmpty = 0;
        }

        const ptbm = this.m_aptbm[bFog];
        if (ptbm === null) {
          xTile += gcx;
          continue;
        }
        ptbm.BltTo(pbm, xTile, yTile);
        xTile += gcx;
      }
      if (cEmpty !== 0) {
        pbm.Fill(xStart, yTile, cEmpty * gcx, gcy, clrBlack);
      }
      ibMap += cbNextScan;
      iInvalid += cfInvalidNextScan;
      yTile += gcx; // (sic — the C++ advances y by gcxTile; square tiles)
      xTile = xTileStart;
    }
  }

  // fogmap.cpp:423-538
  DrawGalaxite(
    pbm: DibBitmapLike & { GetSize(): { cx: number; cy: number } },
    xMap: number,
    yMap: number,
    pupd: UpdateMap,
    fogBytes: Uint8Array,
    ctxMap: number,
    ctyMap: number,
    trMap: Uint8Array,
  ): void {
    const gcx = this.coords.gcxTile;
    const gcy = this.coords.gcyTile;
    const siz = pbm.GetSize();
    const tx = Math.floor(xMap / gcx);
    const ty = Math.floor(yMap / gcy);
    let ctx = Math.floor((siz.cx + gcx - 1) / gcx) + 1;
    if (tx + ctx > ctxMap) ctx = ctxMap - tx;
    let cty = Math.floor((siz.cy + gcy - 1) / gcy) + 1;
    if (ty + cty > ctyMap) cty = ctyMap - ty;

    let ibMap = ty * ctxMap + tx;
    const cbNextScan = ctxMap - ctx;
    let xTile = tx * gcx - xMap;
    let yTile = ty * gcy - yMap;
    const xTileStart = xTile;

    const invalid = pupd.GetInvalidMap();
    const sizMap = pupd.GetMapSize();
    const cfInvalidNextScan = sizMap.cx - ctx;
    let iInvalid = 0;

    for (let tyT = ty; tyT < ty + cty; tyT++) {
      for (let txT = tx; txT < tx + ctx; txT++, ibMap++) {
        const b = fogBytes[ibMap];
        if (!invalid[iInvalid++]) {
          xTile += gcx;
          continue;
        }

        const tt = trMap[ibMap];
        if (((b & kbfGalaxiteMask) === 0 && tt !== kttWall) || (b & kbfFogMask) === kbOpaque) {
          xTile += gcx;
          continue;
        }

        if ((b & kbfGalaxiteMask) !== 0) {
          const ptbm = this.m_aptbmGalax[s_abGxTranslate[(b & kbfGalaxiteMask) >> kcGalaxiteShift]];
          ptbm!.BltTo(pbm, xTile, yTile);
        }

        if (tt === kttWall) {
          // wall segment shape from the 4-neighbor mask (fogmap.cpp:495-519)
          let ifrm = 0;
          if (tyT > 0 && trMap[ibMap - ctxMap] === kttWall) ifrm |= 1;
          if (txT < ctxMap - 1 && trMap[ibMap + 1] === kttWall) ifrm |= 2;
          if (tyT < ctyMap - 1 && trMap[ibMap + ctxMap] === kttWall) ifrm |= 4;
          if (txT > 0 && trMap[ibMap - 1] === kttWall) ifrm |= 8;
          this.m_panidWalls!.DrawFrame(0, ifrm, pbm, xTile, yTile, ksideNeutral);
        }

        xTile += gcx;
      }
      iInvalid += cfInvalidNextScan;
      ibMap += cbNextScan;
      yTile += gcx;
      xTile = xTileStart;
    }
  }
}
